<style scoped>
.c-progress-button {
  opacity: unset !important;
  cursor: not-allowed;
}
@media (max-width: 1024px) {
  .c-sm-spacer {
    margin-top: 1rem;
  }
}
</style>
<script>

import {get_acsrf_token} from "@/methods";

let countries = require("i18n-iso-countries");
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

import CopyableText from "@/components/CopyableText";
import {HalfCircleSpinner} from "epic-spinners";
import {mapGetters} from "vuex";
import {maxLength, minLength, required} from "vuelidate/lib/validators";

export default {
  props: ['plan', 'planInfo'],
  components: {
    // eslint-disable-next-line vue/no-unused-components
    CopyableText,
    HalfCircleSpinner
  },
  validations: {
    billingAddress: {
      firstName: {required},
      lastName: {required},
      line1: {required},
      line2: {},
      country: {required},
      region: {required},
      city: {required},
      zip: {required}
    },
  },
  computed: {
    ...mapGetters([
      'getAccountName',
      'getAccountId',
    ])
  },
  methods: {
    async retrieveBillingAddress() {
      try {
        let response = await fetch(process.env.VUE_APP_ROOT_API + `v1/account/billing-address?lock_state=true`, {
          method: 'GET',
          credentials: 'include'
        });
        if(response.ok) {
          let data = await response.json();
          this.billingAddress.firstName = data.address.first_name;
          this.billingAddress.lastName = data.address.last_name;
          this.billingAddress.line1 = data.address.line1;
          this.billingAddress.line2 = data.address.line2
          this.billingAddress.country = data.address.country;
          this.billingAddress.region = data.address.region;
          this.billingAddress.city = data.address.city;
          this.billingAddress.zip = data.address.zip;

          this.billingAddressLocked = data.locked;

	        if(!this.billingAddressLocked) {
		        this.billingAddress.country = this.billingAddress.country || data.prefill.country;
		        this.billingAddress.region = this.billingAddress.region || data.prefill.region;
	        }
        } else {
          this.error = true;
          throw new Error(`(${this.$vnode.componentOptions.tag}) Failed with API error ${response.status}=${response.statusText} (${response.url})`);
        }
      } catch (error) {
        console.log(`[ERROR] ${error}`);
      }
    },
    async saveBillingAddress() {
      this.$v.billingAddress.$touch();
      if(this.$v.billingAddress.$error) return;
      this.savingBillingAddress = true;
      this.billingAddressLocked = true;

      try {
        let acsrf_token = await get_acsrf_token();
        let payload = {
          acsrf_token: acsrf_token,
          first_name: this.billingAddress.firstName,
          last_name: this.billingAddress.lastName,
          line1: this.billingAddress.line1,
          line2: this.billingAddress.line2,
          country: this.billingAddress.country,
          region: this.billingAddress.region,
          city: this.billingAddress.city,
          zip: this.billingAddress.zip
        };

        let url = new URL(process.env.VUE_APP_ROOT_API + `v1/account/billing-address`);
        let response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(payload),
          credentials: 'include'
        });

        if(!response.ok) {
          if(response.status === 400) {
            let data = await response.json();
            switch(data.error) {
              case 'address-invalid': {
                this.$swal({
                  icon: 'error',
                  text: this.$t('billing.overview.address.save.error'),
                  title: 'This seems like an invalid address'
                });
                break;
              }
              case 'geocode-error': {
                this.$swal({
                  icon: 'error',
                  text: this.$t('billing.overview.address.save.error'),
                  title: 'This seems like an invalid address'
                });
                break;
              }
              case 'geocode-down': {
                this.$swal({
                  icon: 'error',
                  text: this.$t('billing.overview.address.save.error'),
                  title: 'We are having trouble contacting our billing system, please try again'
                });
                break;
              }
              default: {
                throw new Error(`(${this.$vnode.componentOptions.tag}) Failed with API error ${response.status}=${response.statusText} (${response.url})`);
              }
            }
            this.savingBillingAddress = false;
            this.billingAddressLocked = false;
            return;
          }
          throw new Error(`(${this.$vnode.componentOptions.tag}) Failed with API error ${response.status}=${response.statusText} (${response.url})`);
        }
      } catch (error) {
        this.savingBillingAddress = false;
        this.billingAddressLocked = false;

        console.log(`[ERROR] ${error}`);
        this.$swal({
          icon: 'error',
          text: this.$t('billing.overview.address.save.error')
        });
      }
    },
    async processAddressUpdated(data) {
      await this.retrieveBillingAddress();
      this.savingBillingAddress = false;
      if(data.status) {
        this.billingAddressLocked = true;
        this.$swal({
          icon: 'success',
          text: this.$t('billing.overview.address.save.success')
        });
      } else {
        switch(data.error) {
          case 'address-invalid': {
            this.$swal({
              icon: 'error',
              title: this.$t('billing.overview.address.save.error'),
              text: 'This seems like an invalid address'
            });
            break;
          }
          case 'geocode-error': {
            this.$swal({
              icon: 'error',
              title: this.$t('billing.overview.address.save.error'),
              text: 'This seems like an invalid address'
            });
            break;
          }
          case 'geocode-down': {
            this.$swal({
              icon: 'error',
              title: this.$t('billing.overview.address.save.error'),
              text: 'We are having trouble contacting our billing system, please try again'
            });
            break;
          }
          default: {
            this.$swal({
              icon: 'error',
              text: this.$t('billing.overview.address.save.error')
            });
          }
        }
      }
    },
    canContinue() {
      return (this.billingAddressLocked && !this.savingBillingAddress);
    },
    async doContinue() {
      if(!this.canContinue()) return;
      this.processing = true;
      this.$emit('frameCompleted');
    }
  },
  created() {
    try {
      this.$socket.client.on('billing:address:updated', this.processAddressUpdated);
    } catch (e) {
      // nothing
    }
  },
  async mounted() {
    await this.retrieveBillingAddress();
    this.ready = true;
  },
  destroyed() {
    try {
      this.$socket.client.off('billing:address:updated', this.processAddressUpdated);
    } catch (e) {
      // nothing
    }
  },
  data() {
    return {
      ready: true,
      error: false,
      processing: false,
      billingAddress: {
        firstName: null,
        lastName: null,
        line1: null,
        line2: null,
        country: null,
        region: null,
        city: null,
        zip: null
      },
      billingAddressLocked: false,
      savingBillingAddress: false,
    }
  }
};
</script>

<template>
  <div>
    <template v-if="ready">
      <div class="row justify-content-center c-billing-overview">
        <div class="col-lg-9 col-sm-12">
          <div class="card card-body shadow shadow-lg">
            <div class="row">
              <div class="col">
                <div>
                  <h4 class="text-uppercase d-inline-block">
                    {{$t('billing.overview.address.title')}}
                  </h4>
                </div>
                <!-- Personal -->
                <div class="row">
                  <div class="col-lg-6 col-sm-12">
                    <label>{{$t('billing.payment.info.first_name')}}</label>
                    <input
                        class="form-control"
                        v-model="billingAddress.firstName"
                        :class="{ 'is-invalid': $v.billingAddress.firstName.$error, 'disabled': billingAddressLocked }"
                        :disabled="billingAddressLocked"
                        autocorrect="off"
                    />
                  </div>
                  <div class="col-lg-6 col-sm-12">
                    <label>{{$t('billing.payment.info.last_name')}}</label>
                    <input
                        class="form-control"
                        v-model="billingAddress.lastName"
                        :class="{ 'is-invalid': $v.billingAddress.lastName.$error, 'disabled': billingAddressLocked }"
                        :disabled="billingAddressLocked"
                        autocorrect="off"
                    />
                  </div>
                </div>
                <!-- Address -->
                <div class="row mt-2">
                  <div class="col-lg-12 col-sm-12">
                    <label>{{$t('billing.payment.info.line1')}}</label>
                    <input
                        class="form-control"
                        v-model="billingAddress.line1"
                        :placeholder="$t('billing.payment.info.line1_placeholder')"
                        :class="{ 'is-invalid': $v.billingAddress.line1.$error, 'disabled': billingAddressLocked }"
                        :disabled="billingAddressLocked"
                        autocorrect="off"
                    />
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col-lg-12 col-sm-12">
                    <label>{{$t('billing.payment.info.line2')}} (optional)</label>
                    <input
                        class="form-control"
                        v-model="billingAddress.line2"
                        :class="{ 'is-invalid': $v.billingAddress.line2.$error, 'disabled': billingAddressLocked }"
                        :disabled="billingAddressLocked"
                        autocorrect="off"
                    />
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col-lg-6 col-sm-12">
                    <label>{{$t('billing.payment.info.city')}}</label>
                    <input
                        class="form-control"
                        v-model="billingAddress.city"
                        :class="{ 'is-invalid': $v.billingAddress.city.$error, 'disabled': billingAddressLocked }"
                        :disabled="billingAddressLocked"
                        autocorrect="off"
                    />
                  </div>
                  <div class="col-lg-6 col-sm-12">
                    <label>{{$t('billing.payment.info.zip')}}</label>
                    <input
                        class="form-control"
                        v-model="billingAddress.zip"
                        :class="{ 'is-invalid': $v.billingAddress.zip.$error, 'disabled': billingAddressLocked }"
                        :disabled="billingAddressLocked"
                        autocorrect="off"
                    />
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col-lg-6 col-sm-12">
                    <span>{{ $t('billing.payment.info.country') }}</span>
                    <!-- :topCountry="billingAddress.country" -->
                    <country-select
                        className="custom-select"
                        :class="{'is-invalid': $v.billingAddress.country.$error, 'disabled': billingAddressLocked }"
                        :disabled="billingAddressLocked"
                        :placeholder="$t('billing.payment.info.country_prompt')"
                        v-model="billingAddress.country"
                        :country="billingAddress.country"
                        :usei18n=false
                        :removePlaceholder=false
                    />
                  </div>
                  <div class="col-lg-6 col-sm-12">
                    <span>{{ $t('billing.payment.info.region') }}</span>
                    <region-select  v-if="billingAddress.country"
                        className="custom-select"
                        :class="{'is-invalid': $v.billingAddress.region.$error, 'disabled': billingAddressLocked }"
                        :disabled="billingAddressLocked"
                        :placeholder="$t('billing.payment.info.region_prompt')"
                        :country="billingAddress.country"
                        :region="billingAddress.region"
                        v-model="billingAddress.region"
                        :usei18n=false
                        :removePlaceholder=false
                    />
                  </div>
                </div>
                <div class="row mt-4">
                  <div class="col">
                    <div class="text-center mb-3">
                      <template v-if="billingAddressLocked && !savingBillingAddress">
	                      <button class="btn btn-lg btn-dark mr-4" v-on:click="billingAddressLocked = false">
		                      <h5 class="text-uppercase mb-0 text-black">
			                      Change Address
		                      </h5>
	                      </button>
                        <button class="btn btn-lg" :class="{'btn-primary': canContinue(), 'btn-warning': !canContinue(), 'disabled': !canContinue() || processing}" :disabled="!canContinue() || processing" v-on:click="doContinue()">
                          <h5 class="text-uppercase mb-0">
                            <half-circle-spinner
                                v-if="processing"
                                :animation-duration="900"
                                :size="18"
                                class="align-middle d-inline-block"
                            />
                            Continue
                          </h5>
                        </button>
                      </template>
                      <template v-else>
                        <button
                            class="btn btn-outline-success btn-lg"
                            v-if="savingBillingAddress || !billingAddressLocked"
                            :disabled="savingBillingAddress"
                            :class="{'disabled': savingBillingAddress}"
                            v-on:click="saveBillingAddress()"
                        >
                          <half-circle-spinner
                              v-if="savingBillingAddress"
                              :animation-duration="900"
                              :size="18"
                              class="align-middle d-inline-block"
                          />
                          {{$t('billing.overview.address.save.button')}}
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="error">
      <div class="row">
        <div class="col-lg-12 col-sm-12">
          <div class="card border border-danger">
            <div class="card-header bg-transparent border-danger">
              <h5 class="my-0 text-danger">
                <i class="far fa-exclamation-circle mr-3"></i>
                {{$t('error.server.generic.title')}}
              </h5>
            </div>
            <div class="card-body pt-0">
              <h5 class="card-title mt-0"> {{$t('error.server.generic.component')}}</h5>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div style="margin: auto; width: 80px;">
        <half-circle-spinner
            :animation-duration="2000"
            :size="80"
            color="white"
            class="align-middle"
        />
      </div>
    </template>
  </div>
</template>
